/**
 * Budget maths: what an expense really cost (tax included), planned versus actual by category, starter budgets
 * split from a total, and the rounding rules that keep totals adding up. Pure.
 */
import type { BudgetLine, Expense } from "./types";

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Amount paid including tax. Tax is only added when the amount excludes it and a rate was entered. */
export function expenseGross(e: Pick<Expense, "amount" | "taxType" | "taxRate" | "taxIncluded">): number {
  if (e.taxIncluded || e.taxType === "none" || !e.taxRate) return round2(e.amount);
  return round2(e.amount * (1 + e.taxRate / 100));
}

/** Income that has been promised but not received. It is shown apart and left out of profit. */
export const isPledged = (e: Pick<Expense, "kind" | "status">): boolean => e.kind === "income" && e.status === "pledged";

const norm = (s: string | undefined) => (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
export const UNCATEGORISED = "Uncategorised";

export interface BudgetRow {
  /** Display name (first spelling seen). */
  category: string;
  /** Budget line id when this category has a planned amount. */
  lineId?: string;
  planned: number;
  spent: number;
  remaining: number;
  /** 0 to 100+; null when nothing is planned. */
  percentUsed: number | null;
  over: boolean;
}

export interface BudgetReport {
  rows: BudgetRow[];
  planned: number;
  spent: number;
  remaining: number;
  over: boolean;
  /** Expenses left out because they are in another currency. */
  otherCurrency: number;
  /** Spend with no planned line. */
  unbudgeted: number;
}

/**
 * Plans versus actuals. Only costs in the project currency are counted; income is not spend. A category is matched
 * ignoring capital letters and extra spaces. Categories with spend but no planned line still appear (planned 0).
 */
export function budgetReport(lines: readonly BudgetLine[], expenses: readonly Expense[], currency: string): BudgetReport {
  const map = new Map<string, BudgetRow>();
  const row = (name: string): BudgetRow => {
    const key = norm(name) || norm(UNCATEGORISED);
    let r = map.get(key);
    if (!r) { r = { category: name.trim() || UNCATEGORISED, planned: 0, spent: 0, remaining: 0, percentUsed: null, over: false }; map.set(key, r); }
    return r;
  };
  let otherCurrency = 0;
  for (const l of lines) {
    if (l.currency !== currency) { otherCurrency++; continue; }
    const r = row(l.category);
    r.planned = round2(r.planned + l.planned);
    r.lineId = r.lineId ?? l.id;
  }
  for (const e of expenses) {
    if (e.kind !== "cost") continue;
    if (e.currency && e.currency !== currency) { otherCurrency++; continue; }
    const r = row(e.category ?? "");
    r.spent = round2(r.spent + expenseGross(e));
  }
  const rows = Array.from(map.values()).map((r) => {
    const remaining = round2(r.planned - r.spent);
    return { ...r, remaining, percentUsed: r.planned > 0 ? Math.round((r.spent / r.planned) * 100) : null, over: r.spent > r.planned };
  }).sort((a, b) => b.planned - a.planned || a.category.localeCompare(b.category));
  const planned = round2(rows.reduce((s, r) => s + r.planned, 0));
  const spent = round2(rows.reduce((s, r) => s + r.spent, 0));
  return {
    rows, planned, spent, remaining: round2(planned - spent), over: spent > planned, otherCurrency,
    unbudgeted: round2(rows.filter((r) => r.lineId === undefined).reduce((s, r) => s + r.spent, 0)),
  };
}

export const BUDGET_SPLITS: Record<string, { category: string; percent: number }[]> = {
  Wedding: [
    { category: "Venue", percent: 30 }, { category: "Catering", percent: 30 }, { category: "Photography and video", percent: 10 },
    { category: "Music and entertainment", percent: 8 }, { category: "Flowers and decoration", percent: 8 }, { category: "Attire", percent: 5 },
    { category: "Stationery and invitations", percent: 3 }, { category: "Contingency", percent: 6 },
  ],
  Birthday: [
    { category: "Venue", percent: 30 }, { category: "Food and drink", percent: 35 }, { category: "Decorations", percent: 10 },
    { category: "Entertainment", percent: 12 }, { category: "Invitations and favours", percent: 5 }, { category: "Contingency", percent: 8 },
  ],
  "Corporate event": [
    { category: "Venue", percent: 30 }, { category: "Catering", percent: 25 }, { category: "Audio visual", percent: 12 },
    { category: "Speakers and entertainment", percent: 10 }, { category: "Marketing and printing", percent: 8 }, { category: "Staffing", percent: 5 },
    { category: "Travel and accommodation", percent: 5 }, { category: "Contingency", percent: 5 },
  ],
  "Exhibition or trade show": [
    { category: "Venue and floor", percent: 30 }, { category: "Build, power and furniture", percent: 18 }, { category: "Marketing and visitor acquisition", percent: 15 },
    { category: "Catering", percent: 10 }, { category: "Staffing and security", percent: 10 }, { category: "Signage and printing", percent: 5 },
    { category: "Technology and registration", percent: 5 }, { category: "Contingency", percent: 7 },
  ],
  Fundraiser: [
    { category: "Venue", percent: 25 }, { category: "Catering", percent: 30 }, { category: "Entertainment and auction", percent: 15 },
    { category: "Marketing and printing", percent: 10 }, { category: "Decorations", percent: 8 }, { category: "Contingency", percent: 12 },
  ],
  Conference: [
    { category: "Venue", percent: 25 }, { category: "Catering", percent: 25 }, { category: "Audio visual and production", percent: 15 },
    { category: "Speakers and travel", percent: 10 }, { category: "Marketing", percent: 8 }, { category: "Badges, signage and printing", percent: 4 },
    { category: "Staffing", percent: 5 }, { category: "Contingency", percent: 8 },
  ],
};

/** Splits a total across a template so the lines add up exactly to the total (the last line takes the rounding). */
export function splitBudget(total: number, template: string): { category: string; planned: number }[] {
  const split = BUDGET_SPLITS[template];
  if (!split || !Number.isFinite(total) || total <= 0) return [];
  let used = 0;
  return split.map((s, i) => {
    const planned = i === split.length - 1 ? round2(total - used) : round2((total * s.percent) / 100);
    used = round2(used + planned);
    return { category: s.category, planned };
  });
}

/** Categories already used by expenses or lines, for suggestions. */
export function knownCategories(lines: readonly BudgetLine[], expenses: readonly Expense[]): string[] {
  const seen = new Map<string, string>();
  for (const c of [...lines.map((l) => l.category), ...expenses.map((e) => e.category ?? "")]) {
    const k = norm(c);
    if (k && !seen.has(k)) seen.set(k, c.trim());
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/** Brings the old tool's rows (budgeted and actual typed as text) across: planned lines, plus one expense for any actual. */
export function migrateLegacyBudget(
  raw: unknown, parse: (s: string) => number | null, makeId: (prefix: "b" | "e") => string, today: string, currency: string,
): { lines: BudgetLine[]; expenses: Expense[] } {
  const lines: BudgetLine[] = [];
  const expenses: Expense[] = [];
  if (!Array.isArray(raw)) return { lines, expenses };
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const category = typeof o.category === "string" ? o.category.trim() : "";
    if (!category) continue;
    const planned = parse(String(o.budgeted ?? "")) ?? 0;
    const actual = parse(String(o.actual ?? ""));
    lines.push({ id: makeId("b"), category: category.slice(0, 300), planned: Math.max(0, planned), currency });
    if (actual && actual > 0) expenses.push({ id: makeId("e"), date: today, description: `${category} (earlier total)`.slice(0, 300), kind: "cost", category: category.slice(0, 300), amount: actual, currency, taxType: "none", taxIncluded: false });
  }
  return { lines, expenses };
}

/** Income categories offered for events, and the cost categories the Expense Tracker's Event mode offers. */
export const EVENT_INCOME_CATEGORIES = ["Tickets", "Sponsorship", "Exhibitor fees", "Merchandise", "Food and bar", "Donations", "Grants", "Other income"] as const;
export const EVENT_COST_CATEGORIES = ["Venue", "Catering", "Décor", "AV & Production", "Entertainment", "Photography", "Marketing", "Travel", "Staff", "Other"] as const;
